import React from 'react';
import {
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Typography,
  Box,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { getSectionsArray, calculateSectionBadge, PINNED_SECTION } from './dashboardConfig';

/**
 * Sidebar navigation for quick section jumping
 * Always visible on desktop, hidden on mobile (content stacks vertically)
 */
const DashboardSidebar = ({
  expandedSections = [],
  onSectionClick,
  badgeCounts = {},
  pinnedCount = 0
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Hide sidebar on mobile
  if (isMobile) {
    return null;
  }

  const sections = getSectionsArray();

  const handleSectionClick = (sectionId) => {
    // Scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Also expand the section if not already
    onSectionClick(sectionId);
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: 240,
        minWidth: 240,
        minHeight: 'calc(100vh - 200px)',
        position: 'sticky',
        top: 100,
        alignSelf: 'flex-start',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Quick Navigation
        </Typography>
      </Box>

      <List sx={{ py: 1 }}>
        {/* Pinned Favorites Section */}
        {pinnedCount > 0 && (
          <>
            <ListItemButton
              onClick={() => {
                const element = document.getElementById('pinned-favorites');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              sx={{
                py: 1.5,
                '&:hover': { bgcolor: 'warning.light' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Badge badgeContent={pinnedCount} color="warning">
                  <Star sx={{ color: 'warning.main' }} />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={PINNED_SECTION.title}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: 500
                }}
              />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Section Navigation */}
        {sections.map(section => {
          const Icon = section.icon;
          const badge = calculateSectionBadge(section, badgeCounts);
          const isExpanded = expandedSections.includes(section.id);

          return (
            <ListItemButton
              key={section.id}
              onClick={() => handleSectionClick(section.id)}
              selected={isExpanded}
              sx={{
                py: 1.5,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  borderLeft: '3px solid',
                  borderLeftColor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light'
                  }
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Badge
                  badgeContent={badge}
                  color="primary"
                  invisible={badge === 0}
                >
                  <Icon
                    sx={{
                      color: isExpanded ? 'primary.main' : 'text.secondary'
                    }}
                  />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={section.title}
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: isExpanded ? 600 : 400,
                  color: isExpanded ? 'primary.main' : 'text.primary'
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      {/* Section Count Footer */}
      <Divider />
      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary">
          {sections.length} sections • {sections.reduce((acc, s) => acc + s.modules.length, 0)} modules
        </Typography>
      </Box>
    </Paper>
  );
};

export default DashboardSidebar;
